import { Alert, AlertIcon, Button, IconButton, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Stack, Text, useDisclosure } from '@chakra-ui/react';
import GooglePlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-google-places-autocomplete';
import React, { useCallback, useEffect, useState } from 'react';
import { deliveryType, fleetType } from '../../../types';
import { useAppDispatch, useAppSelector } from '../../../hooks';

import { DistanceMatrixService } from '@react-google-maps/api';
import { EditIcon } from '@chakra-ui/icons';
import { updateDelivery } from '../../../store/actions';

type modalUpdaterProps = {
    delivery: deliveryType;
    title: string;
};

export const AddressPickUpModalUpdater = (props: modalUpdaterProps) => {

    const { delivery, title } = props;

    const [origin, setOrigin] = useState<any | null>(null);
    const [originCoords, setOriginCoords] = useState<any | null>(null);
    const [fleet, setFleet] = useState<any>({});
    const [distance, setDistance] = useState<string | null>('');
    const [isUpdatingDelivery, setUpdatingDelivery] = useState<boolean>(false);

    const DRIVING: any = 'DRIVING';

    const { isOpen, onOpen, onClose } = useDisclosure();

    const { company } = useAppSelector(state => state.companyReducer);

    const { fleets } = useAppSelector(state => state.fleetReducer);

    const dispatch = useAppDispatch();

    const { google_api_key } = company ?? {};

    const distance_amount = fleet?.per_km * Number(distance?.split(' ')[0]);

    const sub_total = Number(delivery?.fleet_amount) + Number(delivery?.loaders_amount) + (distance ? Number(distance_amount) : Number(delivery?.distance_amount));
    const vat = sub_total * 0.15;
    const total = sub_total + vat;

    const getOriginCoords = useCallback(() => {
        origin && geocodeByAddress(origin?.label)
            .then((results: Array<any>) => getLatLng(results[0]))
            .then(({ lat, lng }: any) => {
                setOriginCoords({ lat: lat, long: lng });
            }
            );
    }, [origin]);

    const handleUpdateAddress = () => {
        setUpdatingDelivery(true);
        const _distance = distance?.split(' ')[0];
        const values: deliveryType = {
            ...delivery,
            from: {
                address_from: origin.label,
                from_lat: originCoords?.lat,
                from_long: originCoords?.long
            },
            distance: Number(_distance),
            distance_amount,
            vat,
            total
        };
        dispatch(updateDelivery(values))
            .then(() => {
                setUpdatingDelivery(false);
                setDistance('');
                onClose();
            })
            .catch(() => {
                setUpdatingDelivery(false);
                onClose();
            });
    };

    useEffect(() => {
        getOriginCoords();
    }, [origin]);

    useEffect(() => {
        const selectedFleet = fleets?.filter((fleet: fleetType) => fleet.fleet_size === delivery?.fleet)[0];
        setFleet(selectedFleet);
    }, []);

    return (
        <>
            {origin &&
                <DistanceMatrixService
                    options={{
                        destinations: [{ lat: Number(delivery?.to?.to_lat), lng: Number(delivery?.to?.to_long) }],
                        origins: [{ lng: originCoords?.long || delivery?.from?.from_lat, lat: originCoords?.lat || delivery?.from?.from_long }],
                        travelMode: DRIVING
                    }}
                    callback={(response) => {
                        const _distance: any = response?.rows[0]?.elements[0]?.distance?.text;
                        setDistance(_distance);
                    }}
                />
            }
            <IconButton aria-label='Search database' icon={<EditIcon />} size={'xs'} onClick={onOpen} />
            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{title}</ModalHeader>
                    <ModalCloseButton colorScheme={'yellow'} />
                    <ModalBody>
                        <GooglePlacesAutocomplete
                            apiKey={google_api_key}
                            apiOptions={{ region: 'za' }}
                            selectProps={{
                                placeholder: title,
                                origin,
                                onChange: setOrigin
                            }}
                        />
                        <Alert status='info' mt={3} borderRadius={4} fontSize={'sm'}>
                            <AlertIcon />
                            The texts highlighted in green, are new values.
                        </Alert>
                        <Stack bg={'gray.100'} p={2} borderRadius={'md'} mt={3}>
                            <Text fontSize={'sm'}><strong>Address:</strong> {`${delivery?.from?.address_from}`} {distance && <span style={{ color: 'green' }}>- {origin?.label}</span>}</Text>
                            <Text fontSize={'sm'}><strong>Distance:</strong> {`${delivery?.distance} km`} {distance && <span style={{ color: 'green' }}>- {distance}</span>}</Text>
                            <Text fontSize={'sm'}><strong>Distance Amount:</strong> {`R${Number(delivery?.distance_amount).toFixed(2)}`} {distance && <span style={{ color: 'green' }}>- R{distance_amount.toFixed(2)}</span>}</Text>
                            <Text fontSize={'sm'}><strong>Fleet Amount:</strong> {`R${delivery?.fleet_amount?.toFixed(2)}`}</Text>
                            <Text fontSize={'sm'}><strong>Loaders Amount:</strong> {`R${Number(delivery?.loaders_amount).toFixed(2)}`}</Text>
                            <Text fontSize={'sm'}><strong>Vat:</strong> {`R${delivery?.vat?.toFixed(2)}`} {distance && <span style={{ color: 'green' }}>- R{vat.toFixed(2)}</span>}</Text>
                            <Text fontSize={'sm'}><strong>Total Amount:</strong> {`R${Number(delivery?.total).toFixed(2)}`} {distance && <span style={{ color: 'green' }}>- R{total.toFixed(2)}</span>}</Text>
                        </Stack>
                    </ModalBody>

                    <ModalFooter>
                        <Button colorScheme='yellow' variant='outline' mr={3} onClick={onClose}>
                            Close
                        </Button>
                        <Button colorScheme={'yellow'} onClick={handleUpdateAddress} isLoading={isUpdatingDelivery} isDisabled={isUpdatingDelivery || !distance}>Update</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

AddressPickUpModalUpdater.defaultProps = {
    title: 'Modal title'
};

export const AddressDropOffModalUpdater = (props: modalUpdaterProps) => {

    const { delivery, title } = props;

    const [destination, setDestination] = useState<any | null>(null);
    const [desCoords, setDesCoords] = useState<any | null>(null);
    const [fleet, setFleet] = useState<any>({});
    const [distance, setDistance] = useState<string | null>('');
    const [isUpdatingDelivery, setUpdatingDelivery] = useState<boolean>(false);

    const DRIVING: any = 'DRIVING';

    const { isOpen, onOpen, onClose } = useDisclosure();

    const { company } = useAppSelector(state => state.companyReducer);

    const { fleets } = useAppSelector(state => state.fleetReducer);

    const dispatch = useAppDispatch();

    const { google_api_key } = company ?? {};

    const distance_amount = fleet?.per_km * Number(distance?.split(' ')[0]);

    const sub_total = Number(delivery?.fleet_amount) + Number(delivery?.loaders_amount) + (distance ? Number(distance_amount) : Number(delivery?.distance_amount));
    const vat = sub_total * 0.15;
    const total = sub_total + vat;

    const getDestinationCoords = useCallback(() => {
        destination && geocodeByAddress(destination?.label)
            .then((results: Array<any>) => getLatLng(results[0]))
            .then(({ lat, lng }: any) => {
                setDesCoords({ lat: lat, long: lng });
            }
            );
    }, [destination]);

    const handleUpdateAddress = () => {
        setUpdatingDelivery(true);
        const _distance = distance?.split(' ')[0];
        const values: deliveryType = {
            ...delivery,
            to: {
                address_to: destination.label,
                to_lat: desCoords?.lat,
                to_long: desCoords?.long
            },
            distance: Number(_distance),
            distance_amount,
            vat,
            total
        };
        dispatch(updateDelivery(values))
            .then(() => {
                setUpdatingDelivery(false);
                setDistance('');
                onClose();
            })
            .catch(() => {
                setUpdatingDelivery(false);
                onClose();
            });
    };

    useEffect(() => {
        getDestinationCoords();
    }, [destination]);

    useEffect(() => {
        const selectedFleet = fleets?.filter((fleet: fleetType) => fleet.fleet_size === delivery?.fleet)[0];
        setFleet(selectedFleet);
    }, []);

    return (
        <>
            {destination &&
                <DistanceMatrixService
                    options={{
                        origins: [{ lat: Number(delivery?.from?.from_lat), lng: Number(delivery?.from?.from_long) }],
                        destinations: [{ lng: desCoords?.long || delivery?.to?.to_long, lat: desCoords?.lat || delivery?.to?.to_lat }],
                        travelMode: DRIVING
                    }}
                    callback={(response) => {
                        const _distance: any = response?.rows[0]?.elements[0]?.distance?.text;
                        setDistance(_distance);
                    }}
                />
            }
            <IconButton aria-label='Search database' icon={<EditIcon />} size={'xs'} onClick={onOpen} />
            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{title}</ModalHeader>
                    <ModalCloseButton colorScheme={'yellow'} />
                    <ModalBody>
                        <GooglePlacesAutocomplete
                            apiKey={google_api_key}
                            apiOptions={{ region: 'za' }}
                            selectProps={{
                                placeholder: title,
                                origin,
                                onChange: setDestination
                            }}
                        />
                        <Alert status='info' mt={3} borderRadius={4} fontSize={'sm'}>
                            <AlertIcon />
                            The texts highlighted in green, are new values.
                        </Alert>
                        <Stack bg={'gray.100'} p={2} borderRadius={'md'} mt={3}>
                            <Text fontSize={'sm'}><strong>Address:</strong> {`${delivery?.to?.address_to}`} {distance && <span style={{ color: 'green' }}>- {destination?.label}</span>}</Text>
                            <Text fontSize={'sm'}><strong>Distance:</strong> {`${delivery?.distance} km`} {distance && <span style={{ color: 'green' }}>- {distance}</span>}</Text>
                            <Text fontSize={'sm'}><strong>Distance Amount:</strong> {`R${Number(delivery?.distance_amount).toFixed(2)}`} {distance && <span style={{ color: 'green' }}>- R{distance_amount.toFixed(2)}</span>}</Text>
                            <Text fontSize={'sm'}><strong>Fleet Amount:</strong> {`R${delivery?.fleet_amount?.toFixed(2)}`}</Text>
                            <Text fontSize={'sm'}><strong>Loaders Amount:</strong> {`R${Number(delivery?.loaders_amount).toFixed(2)}`}</Text>
                            <Text fontSize={'sm'}><strong>Vat:</strong> {`R${delivery?.vat?.toFixed(2)}`} {distance && <span style={{ color: 'green' }}>- R{vat.toFixed(2)}</span>}</Text>
                            <Text fontSize={'sm'}><strong>Total Amount:</strong> {`R${Number(delivery?.total).toFixed(2)}`} {distance && <span style={{ color: 'green' }}>- R{total.toFixed(2)}</span>}</Text>
                        </Stack>
                    </ModalBody>

                    <ModalFooter>
                        <Button colorScheme='yellow' variant='outline' mr={3} onClick={onClose}>
                            Close
                        </Button>
                        <Button colorScheme={'yellow'} onClick={handleUpdateAddress} isLoading={isUpdatingDelivery} isDisabled={!distance || isUpdatingDelivery}>Update</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

AddressDropOffModalUpdater.defaultProps = {
    title: 'Modal title'
};